/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  Grid,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Approval as ApprovalIcon,
  ThumbUp as ThumbUpIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  TimerOff as TimerOffIcon,
  Drafts as DraftsIcon,
} from "@mui/icons-material";
import {
  fetchSalaryConfigurations,
  deleteExistingSalaryConfiguration,
  approveExistingSalaryConfiguration,
} from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationActions";
import {
  selectSalaryConfigurations,
  selectSalaryConfigurationLoading,
  selectSalaryConfigurationApproving,
} from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationSelectors";
import AddSalaryConfigurationModal from "./AddSalaryConfigurationModal";
import EditSalaryConfigurationModal from "./EditSalaryConfigurationModal";
import salaryConfigSvg from "assets/folders/salary-config.svg";

const SalaryConfiguration = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const salaryConfigurations = useSelector(selectSalaryConfigurations);
  const loading = useSelector(selectSalaryConfigurationLoading);
  const approving = useSelector(selectSalaryConfigurationApproving);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [configToApprove, setConfigToApprove] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedConfigForView, setSelectedConfigForView] = useState(null);

  useEffect(() => {
    dispatch(fetchSalaryConfigurations());
  }, [dispatch]);

  const filteredConfigurations = salaryConfigurations.filter((config) => {
    const matchesSearch =
      searchTerm === "" ||
      `${config.first_name} ${config.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      config.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.department_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      config.level?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || config.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  const getStatusColor = (status) => {
    const colors = {
      active: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
      },
      draft: {
        bg: "#fef3c7",
        text: "#92400e",
        darkBg: "rgba(245,158,11,0.25)",
        darkText: "#fde68a",
        icon: <DraftsIcon sx={{ fontSize: 14 }} />,
      },
      inactive: {
        bg: "#fee2e2",
        text: "#991b1b",
        darkBg: "rgba(239,68,68,0.25)",
        darkText: "#fca5a5",
        icon: <CancelIcon sx={{ fontSize: 14 }} />,
      },
       expired: {
        bg: "#fafafa",
        text: "#575640",
        darkBg: "rgba(15, 15, 15, 0.25)",
        darkText: "#c0c0c0",
        icon: <TimerOffIcon sx={{ fontSize: 14 }} />,
      },
    };
    return colors[status] || colors.draft;
  };

  const StatusBadge = ({ status }) => {
    const colors = getStatusColor(status);
    const labels = {
      active: "Active",
      draft: "Draft",
      inactive: "Inactive",
      expired: "Expired",
    };
    return (
      <Chip
        icon={colors.icon}
        label={labels[status] || status}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: colors.darkBg,
            color: colors.darkText,
          }),
        }}
      />
    );
  };

  const handleDeleteClick = (config) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (configToDelete) {
      setDeleting(true);
      const result = await dispatch(
        deleteExistingSalaryConfiguration(configToDelete.id),
      );
      if (result.success) {
        dispatch(fetchSalaryConfigurations());
        setDeleteDialogOpen(false);
        setConfigToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  const handleApproveClick = (config) => {
    setConfigToApprove(config);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (configToApprove) {
      const result = await dispatch(
        approveExistingSalaryConfiguration(configToApprove.id),
      );
      if (result.success) {
        dispatch(fetchSalaryConfigurations());
        setApproveDialogOpen(false);
        setConfigToApprove(null);
      }
    }
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setConfigToApprove(null);
  };

  const handleViewClick = (config) => {
    setSelectedConfigForView(config);
    setViewDialogOpen(true);
  };

  const calculateTotalCTC = (config) => {
    let total = parseFloat(config.custom_basic_salary) || 0;
    total += parseFloat(config.custom_hra_amount) || 0;
    total += parseFloat(config.custom_conveyance_amount) || 0;
    total += parseFloat(config.custom_education_allowance_amount) || 0;
    total += parseFloat(config.custom_transport_allowance_amount) || 0;
    total += parseFloat(config.custom_fixed_allowance_amount) || 0;
    total += parseFloat(config.custom_commission_amount) || 0;
    total += parseFloat(config.additional_allowance_1_amount) || 0;
    total += parseFloat(config.additional_allowance_2_amount) || 0;
    total += parseFloat(config.additional_allowance_3_amount) || 0;
    return total;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={salaryConfigSvg}
              alt="Salary Configurations"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Employee Salary Configurations
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                Manage individual employee salary structures
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Assign Salary
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by employee name, email or department..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "active", "draft", "inactive", "expired"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "#f59e0b" }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading salary configurations...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    ID & Tenant
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Employee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Level/Grade
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Basic Salary
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Total CTC
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Effective Period
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConfigurations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <MoneyIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        }}
                      >
                        No salary configurations found. Click "Assign Salary" to
                        get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConfigurations.map((config, index) => (
                    <TableRow
                      key={config.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#f59e0b",
                            }}
                          >
                            ID: {config.id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#98a3b8"
                                  : "#4c5462",
                            }}
                          >
                            Tenant: {config.tenant_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background:
                                theme.palette.mode === "dark"
                                  ? "linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(217,119,6,0.2) 100%)"
                                  : "linear-gradient(135deg, #fef3c7 0%, #fef08a 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PersonIcon sx={{ color: "#f59e0b" }} />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#ffffff"
                                    : "#111827",
                              }}
                            >
                              {config.first_name} {config.last_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#6b7280"
                                    : "#9ca3af",
                              }}
                            >
                              {config.email}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#f59e0b"
                                    : "#b8790c",
                                fontWeight: 500,
                                mt: 0.5,
                              }}
                            >
                              Emp ID: {config.employee_id}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: "0.875rem" }}>
                          {config.department_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                          >
                            {config.level}
                          </Typography>
                          <Chip
                            label={`Grade ${config.grade}`}
                            size="small"
                            sx={{
                              width: "fit-content",
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#f59e0b",
                          }}
                        >
                          {formatCurrency(config.custom_basic_salary)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                        >
                          {formatCurrency(calculateTotalCTC(config))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#acb6c7"
                                  : "#696e75",
                            }}
                          >
                            <b>From: </b> {formatDate(config.effective_from)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#acb6c7"
                                  : "#696e75",
                            }}
                          >
                            <b>To: </b> {formatDate(config.effective_to)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={config.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => handleViewClick(config)}
                              size="small"
                              sx={{
                                color: "#3b82f6",
                                "&:hover": { backgroundColor: "#dbeafe" },
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => {
                                setSelectedConfig(config);
                                setShowEditModal(true);
                              }}
                              size="small"
                              sx={{
                                color: "#f59e0b",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(245,158,11,0.2)"
                                      : "#fffbeb",
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDeleteClick(config)}
                              size="small"
                              sx={{
                                color: "#ef4444",
                                "&:hover": { backgroundColor: "#fee2e2" },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                          {config.status === "draft" && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleApproveClick(config)}
                              disabled={approving}
                              startIcon={<ThumbUpIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                backgroundColor: "#10b981",
                                textTransform: "none",
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                borderRadius: "8px",
                                padding: "4px 12px",
                                minWidth: "auto",
                                "&:hover": {
                                  backgroundColor: "#059669",
                                },
                              }}
                            >
                              Approve
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Count Summary */}
      {!loading && salaryConfigurations.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          <Typography variant="body2">
            Total Configurations: {salaryConfigurations.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredConfigurations.length} of{" "}
            {salaryConfigurations.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading &&
        filteredConfigurations.length === 0 &&
        salaryConfigurations.length > 0 && (
          <Paper
            sx={{
              mt: 4,
              p: 4,
              textAlign: "center",
              borderRadius: "16px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                mb: 2,
              }}
            >
              No salary configurations match your search criteria
            </Typography>
            <Button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
              sx={{ color: "#f59e0b", textTransform: "none" }}
            >
              Clear filters
            </Button>
          </Paper>
        )}

      {/* Modals */}
      <AddSalaryConfigurationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchSalaryConfigurations())}
      />

      <EditSalaryConfigurationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedConfig(null);
        }}
        salaryConfig={selectedConfig}
        onSuccess={() => dispatch(fetchSalaryConfigurations())}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Delete Salary Configuration
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to delete the salary configuration for
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#f59e0b",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{configToDelete?.first_name} {configToDelete?.last_name}"
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleApproveCancel}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleApproveCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#10b981", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Approve Salary Configuration
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to approve the salary configuration for
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#10b981",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{configToApprove?.first_name} {configToApprove?.last_name}"
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#6b7280",
              mb: 2,
            }}
          >
            Once approved, this salary configuration will become active for
            payroll processing.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleApproveCancel}
              disabled={approving}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveConfirm}
              disabled={approving}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              }}
            >
              {approving ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Approve"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
        }}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            overflow: "hidden",
            maxWidth: "600px",
            margin: "16px",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {selectedConfigForView && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Gradient Top Bar */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                height: "5px",
              }}
            />

            {/* Header with SVG */}
            <Box
              sx={{
                p: 2.5,
                pr: 5,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(30,41,59,0.5)"
                    : "rgba(255,251,235,0.5)",
                position: "relative",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ alignItems: "center" }}
              >
                <Box
                  component="img"
                  src={salaryConfigSvg}
                  alt="Details"
                  sx={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 4px rgba(245,158,11,0.3))",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  Salary Configuration Details
                </Typography>
              </Stack>

              <IconButton
                onClick={() => setViewDialogOpen(false)}
                sx={{
                  position: "absolute",
                  top: 18,
                  right: 18,
                  width: 32,
                  height: 32,
                  borderRadius: "10px",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  "&:hover": {
                    background: "#f59e0b",
                    color: "#ffffff",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Single Scrollable Content */}
            <Box
              sx={{
                maxHeight: "calc(80vh - 200px)",
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-track": {
                  background:
                    theme.palette.mode === "dark" ? "#1e293b" : "#e2e8f0",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#f59e0b",
                  borderRadius: "10px",
                },
              }}
            >
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  {/* Row 1: Employee Information + Department Details */}
                  <Grid
                    container
                    spacing={2}
                    display="flex"
                    alignItems="stretch"
                    sx={{ width: "100%", margin: 0 }}
                  >
                    <Grid item xs={12} md={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          height: "100%",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              borderRadius: "2px",
                              background: "#f59e0b",
                            }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Employee Information
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Employee Name
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {selectedConfigForView.first_name}{" "}
                              {selectedConfigForView.last_name}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Employee ID
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {selectedConfigForView.employee_id}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Email
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {selectedConfigForView.email}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Department
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {selectedConfigForView.department_name}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Level / Grade
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {selectedConfigForView.level} / Grade{" "}
                              {selectedConfigForView.grade}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Status
                            </Typography>
                            <StatusBadge
                              status={selectedConfigForView.status}
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Row 2: Compensation Breakdown */}
                  <Grid container spacing={2} sx={{ width: "100%", margin: 0 }}>
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <MoneyIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Compensation Breakdown
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "130px" }}
                            >
                              Basic Salary
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#f59e0b",
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_basic_salary,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "130px" }}
                            >
                              HRA Amount
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_hra_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "130px" }}
                            >
                              Conveyance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_conveyance_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "130px" }}
                            >
                              Education Allowance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_education_allowance_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "130px" }}
                            >
                              Transport Allowance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_transport_allowance_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "130px" }}
                            >
                              Fixed Allowance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_fixed_allowance_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "130px" }}
                            >
                              Commission Amount
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_commission_amount,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{
                              gap: "16px",
                              pt: 1,
                              borderTop: "1px dashed #e2e8f0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: "#f59e0b",
                                minWidth: "130px",
                              }}
                            >
                              Total CTC
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: "#10b981",
                                fontSize: "0.9rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                calculateTotalCTC(selectedConfigForView),
                              )}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              borderRadius: "2px",
                              background: "#f59e0b",
                            }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Reimbursements & Benefits
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Fuel Reimbursement
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_fuel_reimbursement,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Telephone Reimbursement
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_telephone_reimbursement,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Driver Reimbursement
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_driver_reimbursement,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Vehicle Maintenance
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_vehicle_maintenance_reimbursement,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              LTA Reimbursement
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_lta_reimbursement,
                              )}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "140px" }}
                            >
                              Gratuity Amount
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                textAlign: "right",
                              }}
                            >
                              {formatCurrency(
                                selectedConfigForView.custom_gratuity_amount,
                              )}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Row 3: Additional Allowances & Deductions */}
                  {(selectedConfigForView.additional_allowance_1_name ||
                    selectedConfigForView.additional_deduction_1_name) && (
                    <Grid
                      container
                      spacing={2}
                      sx={{ width: "100%", margin: 0 }}
                    >
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "20px",
                            background:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.6)"
                                : "#fefce8",
                            border: `1px solid ${
                              theme.palette.mode === "dark"
                                ? "#334155"
                                : "#fef3c7"
                            }`,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 2 }}
                          >
                            <Box
                              sx={{
                                width: 4,
                                height: 20,
                                borderRadius: "2px",
                                background: "#f59e0b",
                              }}
                            />
                            <Typography
                              sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                            >
                              Additional Allowances
                            </Typography>
                          </Stack>
                          <Stack spacing={1.5}>
                            {selectedConfigForView.additional_allowance_1_name && (
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: "16px" }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af", minWidth: "140px" }}
                                >
                                  {
                                    selectedConfigForView.additional_allowance_1_name
                                  }
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    textAlign: "right",
                                  }}
                                >
                                  {formatCurrency(
                                    selectedConfigForView.additional_allowance_1_amount,
                                  )}
                                </Typography>
                              </Stack>
                            )}
                            {selectedConfigForView.additional_allowance_2_name && (
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: "16px" }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af", minWidth: "140px" }}
                                >
                                  {
                                    selectedConfigForView.additional_allowance_2_name
                                  }
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    textAlign: "right",
                                  }}
                                >
                                  {formatCurrency(
                                    selectedConfigForView.additional_allowance_2_amount,
                                  )}
                                </Typography>
                              </Stack>
                            )}
                            {selectedConfigForView.additional_allowance_3_name && (
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: "16px" }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af", minWidth: "140px" }}
                                >
                                  {
                                    selectedConfigForView.additional_allowance_3_name
                                  }
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    textAlign: "right",
                                  }}
                                >
                                  {formatCurrency(
                                    selectedConfigForView.additional_allowance_3_amount,
                                  )}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "20px",
                            background:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.6)"
                                : "#fefce8",
                            border: `1px solid ${
                              theme.palette.mode === "dark"
                                ? "#334155"
                                : "#fef3c7"
                            }`,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 2 }}
                          >
                            <Box
                              sx={{
                                width: 4,
                                height: 20,
                                borderRadius: "2px",
                                background: "#f59e0b",
                              }}
                            />
                            <Typography
                              sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                            >
                              Additional Deductions
                            </Typography>
                          </Stack>
                          <Stack spacing={1.5}>
                            {selectedConfigForView.additional_deduction_1_name && (
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: "16px" }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af", minWidth: "140px" }}
                                >
                                  {
                                    selectedConfigForView.additional_deduction_1_name
                                  }
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "#ef4444",
                                    textAlign: "right",
                                  }}
                                >
                                  -
                                  {formatCurrency(
                                    selectedConfigForView.additional_deduction_1_amount,
                                  )}
                                </Typography>
                              </Stack>
                            )}
                            {selectedConfigForView.additional_deduction_2_name && (
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: "16px" }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af", minWidth: "140px" }}
                                >
                                  {
                                    selectedConfigForView.additional_deduction_2_name
                                  }
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "#ef4444",
                                    textAlign: "right",
                                  }}
                                >
                                  -
                                  {formatCurrency(
                                    selectedConfigForView.additional_deduction_2_amount,
                                  )}
                                </Typography>
                              </Stack>
                            )}
                            {selectedConfigForView.additional_deduction_3_name && (
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: "16px" }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#9ca3af", minWidth: "140px" }}
                                >
                                  {
                                    selectedConfigForView.additional_deduction_3_name
                                  }
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "#ef4444",
                                    textAlign: "right",
                                  }}
                                >
                                  -
                                  {formatCurrency(
                                    selectedConfigForView.additional_deduction_3_amount,
                                  )}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}

                  {/* Timestamps */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.6)"
                          : "#fefce8",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <EventIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        Effective Period & Timestamps
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px", mb: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "80px" }}
                          >
                            Effective From
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              textAlign: "right",
                            }}
                          >
                            {formatDate(selectedConfigForView.effective_from)}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px" }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "80px" }}
                          >
                            Effective To
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              textAlign: "right",
                            }}
                          >
                            {formatDate(selectedConfigForView.effective_to)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px", mb: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "100px" }}
                          >
                            Created At
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              textAlign: "right",
                            }}
                          >
                            {formatDateTime(selectedConfigForView.created_at)}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px", mb: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "100px" }}
                          >
                            Updated At
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              opacity: 0.7,
                              textAlign: "right",
                            }}
                          >
                            {formatDateTime(selectedConfigForView.updated_at)}
                          </Typography>
                        </Stack>
                        {selectedConfigForView.approved_by && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px", mb: 1 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Approved By
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                textAlign: "right",
                                color: "#10b981",
                                fontWeight: 500,
                              }}
                            >
                              {selectedConfigForView.approved_by}
                            </Typography>
                          </Stack>
                        )}
                        {selectedConfigForView.approved_at && (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "100px" }}
                            >
                              Approved At
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                textAlign: "right",
                                color: "#10b981",
                              }}
                            >
                              {formatDateTime(
                                selectedConfigForView.approved_at,
                              )}
                            </Typography>
                          </Stack>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Stack>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => setViewDialogOpen(false)}
                variant="contained"
                sx={{
                  borderRadius: "14px",
                  px: 3,
                  py: 0.7,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </motion.div>
        )}
      </Dialog>
    </Box>
  );
};

export default SalaryConfiguration;
